import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  MONTHS,
  MONTH_LABELS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type CalAdj,
  type DayOfWeekParams,
  type RuleFormProps,
  majorStrToMinorOpt,
  minorToMajorStr,
  pctStrToNumOpt,
} from '@/lib/pricingRules';

// Per-weekday % / absolute (€) adjustment editor, plus an optional month
// filter. Mirrors the local string-draft model from DateSpecificRuleForm
// (the reference form): draft state is seeded once from `params`, and the
// typed `days` map (+ optional `months` filter) is rebuilt and emitted on
// every change. A weekday with neither pct nor abs entered is omitted from
// the emitted map; the backend requires `days` non-empty, but enforcing
// that is left to the parent/save path per the form contract — this editor
// just reflects whatever `params` it was seeded with.

interface DayDraft {
  pct: string;
  abs: string;
}

function draftFor(adj: CalAdj | undefined): DayDraft {
  return {
    pct: adj?.pct != null ? String(adj.pct) : '',
    abs: minorToMajorStr(adj?.abs_minor),
  };
}

function draftsToDays(drafts: Record<string, DayDraft>): Record<string, CalAdj> {
  const days: Record<string, CalAdj> = {};
  for (const w of WEEKDAYS) {
    const d = drafts[w];
    if (!d) continue;
    const adj: CalAdj = {};
    const p = pctStrToNumOpt(d.pct);
    if (typeof p === 'number') adj.pct = p;
    const a = majorStrToMinorOpt(d.abs);
    if (typeof a === 'number') adj.abs_minor = a;
    if (adj.pct !== undefined || adj.abs_minor !== undefined) days[w] = adj;
  }
  return days;
}

export function DayOfWeekRuleForm({ params, onChange }: RuleFormProps<DayOfWeekParams>) {
  const [dayDrafts, setDayDrafts] = useState<Record<string, DayDraft>>(() => {
    const init: Record<string, DayDraft> = {};
    for (const w of WEEKDAYS) {
      init[w] = draftFor(params.days?.[w]);
    }
    return init;
  });
  const [months, setMonths] = useState<number[]>(() =>
    [...(params.months ?? [])].sort((a, b) => a - b),
  );

  function emit(days: Record<string, DayDraft>, nextMonths: number[]) {
    onChange({
      days: draftsToDays(days),
      ...(nextMonths.length > 0 ? { months: nextMonths } : {}),
    });
  }

  function patchDay(key: string, p: Partial<DayDraft>) {
    const current = dayDrafts[key] ?? { pct: '', abs: '' };
    const next = { ...dayDrafts, [key]: { ...current, ...p } };
    setDayDrafts(next);
    emit(next, months);
  }

  function toggleMonth(m: number) {
    const next = months.includes(m) ? months.filter((x) => x !== m) : [...months, m].sort((a, b) => a - b);
    setMonths(next);
    emit(dayDrafts, next);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {WEEKDAYS.map((w) => {
          const d = dayDrafts[w] ?? { pct: '', abs: '' };
          return (
            <div
              key={w}
              className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
            >
              <div className="mb-2 text-sm font-semibold text-[var(--text-heading)]">
                {WEEKDAY_LABELS[w]}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Adjustment (%)"
                  type="number"
                  step="0.1"
                  value={d.pct}
                  onChange={(e) => patchDay(w, { pct: e.target.value })}
                  placeholder="e.g. 20"
                />
                <Input
                  label="Absolute (€)"
                  type="number"
                  step="0.01"
                  value={d.abs}
                  onChange={(e) => patchDay(w, { abs: e.target.value })}
                  placeholder="optional"
                />
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-[var(--text-heading)]">
          Months (optional filter)
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {MONTHS.map((m) => (
            <Checkbox
              key={m}
              label={MONTH_LABELS[m]}
              checked={months.includes(m)}
              onChange={() => toggleMonth(m)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
